/**
 * A film the trainer chose, turned into the file that will be sent — or the reason it cannot be.
 *
 * The raw sanity gate here, the reading and encoding in `compressVideo.worker` (a module worker, so
 * a two-minute encode never blocks the page), the choice of what to send in `videoFile.deliveryOf`.
 *
 * ── Cancelling terminates the worker ────────────────────────────────────────────────────────
 * A film replaced or removed while it is still encoding aborts `signal`, and the worker is
 * terminated there and then — WebCodecs encoders do not stop because nobody is waiting for them.
 * The promise then rejects with the signal's reason, so a caller awaiting it (a save that was
 * waiting for this film) learns the film is gone rather than waiting for ever.
 *
 * ── A browser with no worker, or none that loads, is `unsupportedBrowser` ───────────────────
 * Without the worker nothing measures the film, and the slot needs its duration, so nothing can be
 * sent. The sentence says so; it does not pretend the file is broken.
 */

import { VIDEO_CLASS } from './videoClass';
import {
  abortReason,
  deliveryOf,
  rawVideoRefusal,
  videoFileName,
  type VideoRefusal,
} from './videoFile';
import type {
  CompressVideoMessage,
  CompressVideoOutcome,
  CompressVideoRequest,
} from './compressVideo.worker';

export type PreparedVideo =
  | {
      readonly ok: true;
      /** What is sent: always `video/mp4`, at most 50 MiB. */
      readonly file: File;
      /** Measured from the film itself, in seconds (not yet rounded for the slot). */
      readonly durationSeconds: number;
    }
  | { readonly ok: false; readonly refusal: VideoRefusal };

export interface PrepareVideoOptions {
  /** 0 to 1 while the browser encodes. Not called when there is nothing to encode. */
  readonly onProgress?: (fraction: number) => void;
  readonly signal?: AbortSignal;
}

function runWorker(file: File, options: PrepareVideoOptions): Promise<CompressVideoOutcome | null> {
  return new Promise((resolve, reject) => {
    const { signal, onProgress } = options;
    if (signal?.aborted === true) {
      reject(abortReason(signal));
      return;
    }
    let worker: Worker;
    try {
      worker = new Worker(new URL('./compressVideo.worker.ts', import.meta.url), {
        type: 'module',
      });
    } catch {
      resolve(null);
      return;
    }
    const stop = () => {
      worker.terminate();
      reject(abortReason(signal));
    };
    signal?.addEventListener('abort', stop, { once: true });
    const finish = (outcome: CompressVideoOutcome | null) => {
      signal?.removeEventListener('abort', stop);
      worker.terminate();
      resolve(outcome);
    };
    worker.addEventListener('message', (event: MessageEvent<CompressVideoMessage>) => {
      if (event.data.type === 'progress') onProgress?.(event.data.fraction);
      else finish(event.data.outcome);
    });
    // A worker that fails to load (an old browser, a blocked script) never answers; say so.
    worker.addEventListener('error', () => {
      finish(null);
    });
    const request: CompressVideoRequest = { file };
    worker.postMessage(request);
  });
}

/** The film to send, or why this one cannot be sent. Rejects only when `signal` aborts. */
export async function prepareVideo(
  file: File,
  options: PrepareVideoOptions = {},
): Promise<PreparedVideo> {
  const raw = rawVideoRefusal(file);
  if (raw !== null) return { ok: false, refusal: raw };

  const outcome = await runWorker(file, options);
  if (outcome === null) return { ok: false, refusal: 'unsupportedBrowser' };
  if (outcome.kind === 'refused') return { ok: false, refusal: outcome.refusal };

  // The one copy the encoded bytes get: the worker transferred them, and the File is made here.
  // Not converted, the outcome itself carries the refusal that stands unless the original may go.
  const encode =
    outcome.kind === 'converted'
      ? new File([outcome.buffer], videoFileName(file.name), { type: VIDEO_CLASS.contentType })
      : outcome;
  const delivery = deliveryOf(file, encode);
  if ('refusal' in delivery) return { ok: false, refusal: delivery.refusal };

  const sent = delivery.send === 'converted' && encode instanceof File ? encode : file;
  return { ok: true, file: sent, durationSeconds: outcome.durationSeconds };
}
